import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useEmailMeeting } from "../../hooks/useMeetings";
import { formatMeetingDate, uniqueEmails } from "../../utils/meetingFormat";
import {
  Btn,
  Checkbox,
  errorMessage,
  SectionLabel,
  SlideOver,
  ui,
} from "../ui";

const MAX_RECIPIENTS = 30;

// Slides in from the right. Sends the summary and notes now to whoever is ticked.
export function EmailNotesPanel({ meeting, open, onClose }) {
  const { apiUser } = useAuth();
  const me = apiUser?.email ?? "";
  const email = useEmailMeeting(meeting.sqid);
  const attendees = (meeting.attendees ?? []).filter(
    (p) => p.email && p.email.toLowerCase() !== me.toLowerCase(),
  );
  const [picked, setPicked] = useState([]);
  const [sent, setSent] = useState(false);
  const { reset } = email;

  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    setPicked(me ? [me] : []);
    setSent(false);
    reset();
  }, [open, me, reset]);

  if (!open) return null;

  const emails = uniqueEmails(picked);
  const recap = meeting.email_recap;

  const toggle = (address, on) =>
    setPicked(on ? [...picked, address] : picked.filter((x) => x !== address));

  const send = () =>
    email.mutate(emails, {
      onSuccess: () => {
        setSent(true);
        setTimeout(() => closeRef.current(), 1200);
      },
    });

  return (
    <SlideOver label="Email notes" onClose={onClose}>
      <p style={{ ...ui.muted, margin: 0, fontSize: "13px", lineHeight: 1.5 }}>
        Sends the summary and meeting notes now. Replies come back to you.
      </p>

      <div>
        {me && (
          <Checkbox
            checked={picked.includes(me)}
            onChange={(on) => toggle(me, on)}
          >
            Me
            <span style={{ ...ui.muted, display: "block", fontSize: "12px" }}>
              {me}
            </span>
          </Checkbox>
        )}
      </div>

      {attendees.length > 0 ? (
        <>
          <SectionLabel style={{ marginTop: "6px" }}>
            From the calendar invite
          </SectionLabel>
          <div>
            {attendees.map((p) => (
              <Checkbox
                key={p.email}
                checked={picked.includes(p.email)}
                onChange={(on) => toggle(p.email, on)}
              >
                {p.name || p.email}
                {p.name && (
                  <span
                    style={{
                      ...ui.muted,
                      display: "block",
                      fontSize: "12px",
                    }}
                  >
                    {p.email}
                  </span>
                )}
              </Checkbox>
            ))}
          </div>
        </>
      ) : (
        <p style={{ ...ui.muted, margin: 0, fontSize: "12px" }}>
          This meeting wasn't linked to a calendar event, so there's no list of
          attendees.
        </p>
      )}

      {recap?.sent_at && (
        <p
          style={{
            ...ui.muted,
            margin: 0,
            fontSize: "12px",
            lineHeight: 1.5,
          }}
        >
          Sent to {recap.emails.join(", ")}. Last sent{" "}
          {formatMeetingDate(recap.sent_at)}.
        </p>
      )}
      {emails.length > MAX_RECIPIENTS && (
        <p style={ui.error}>Up to {MAX_RECIPIENTS} people at a time.</p>
      )}
      {email.error && (
        <p style={ui.error}>
          {email.error.response?.data?.code === "no_summary"
            ? "There's no summary to send yet."
            : errorMessage(email.error)}
        </p>
      )}

      <div
        style={{
          marginTop: "auto",
          display: "flex",
          justifyContent: "flex-end",
          gap: "8px",
          paddingTop: "12px",
        }}
      >
        <Btn variant="ghost" onClick={onClose} disabled={email.isLoading}>
          Cancel
        </Btn>
        <Btn
          variant="primary"
          loading={email.isLoading}
          disabled={sent || !emails.length || emails.length > MAX_RECIPIENTS}
          onClick={send}
        >
          {sent
            ? "Sent"
            : `Send to ${emails.length} ${emails.length === 1 ? "person" : "people"}`}
        </Btn>
      </div>
    </SlideOver>
  );
}
